import React from 'react';

/**
 * Retrieves input data from a form and returns it as a JSON object.
 * https://learnwithjason.dev/blog/get-form-values-as-json/
 */
export const formToJSON = (elements: HTMLFormElement): Record<string, string> =>
  [].reduce.call(
    elements,
    (data, element: {name: string; value: string}) => {
      // @ts-ignore 3rd party code
      (data as Record<string, string>)[element.name] = element.value;
      return data;
    },
    {},
  ) as Record<string, string>;

const injectedScriptCache: string[] = [];

const injectScript = (src: string): Promise<void> => {
  return new Promise((resolve, reject) => {
    if (injectedScriptCache.includes(src)) {
      return resolve();
    }

    injectedScriptCache.push(src);

    const script = document.createElement('script');
    script.onload = () => resolve();
    script.onerror = reject;
    script.src = src;
    document.head.appendChild(script);
  });
};

/**
 * Asyncronously insert script tags into the global document header.
 *
 * @param {string[]} sources
 */
export const injectScripts = async (sources: string[]): Promise<void> => {
  await Promise.all(sources.map(src => injectScript(src)));
};

/**
 * Creates JSS for allowing a certain number of lines then truncating it
 * @param lines
 */
export const makeLineClampJss = (lines: number) =>
  ({
    display: '-webkit-box !important',
    WebkitLineClamp: lines,
    WebkitBoxOrient: 'vertical',
  }) as const;

/**
 * Use a Typography element from pg-ui.
 * Pass ref={textRef} and lineClamp={clampHeight} into the Typography element.
 * Pass toggleExpanded as your Show More/Less button's action.
 */
export const useShowMore = (lineClamp = 2, hasDynamicText = false) => {
  const [showExpandLink, setShowExpandLink] = React.useState(false);
  const [expanded, setExpanded] = React.useState(false);
  const textRef = React.useRef<HTMLParagraphElement | null>(null);
  const toggleExpanded = () => setExpanded(!expanded);
  const clampHeight = expanded ? 1000 : lineClamp;

  React.useEffect(
    () => {
      if (textRef?.current) {
        return textRef.current.scrollHeight > textRef.current.clientHeight
          ? setShowExpandLink(true)
          : setShowExpandLink(false);
      }
    },
    // to allow an initially hidden "more" button to display after first mount
    // useful for builder previews where element content is dynamic
    hasDynamicText ? undefined : [],
  );
  return {textRef, showExpandLink, expanded, toggleExpanded, clampHeight};
};
